/**
 * useNoteSigning Hook
 *
 * Manages note signing workflow including:
 * - Sign note (with electronic signature when the org enables note signing)
 * - Complete note (without signature when the org disables note signing)
 * - Amend completed note
 * - Signing onboarding flow
 *
 * Handles all signing-related mutations, analytics, and user feedback.
 */

import { useCallback, useState } from 'react';
import type { RecordingWindow } from '@features/encounters/components/OfficialTimeSelector/officialTimeModel';
import {
  useLazyGetEncounterQuery,
  useUpdateEncounterMutation,
} from '@features/encounters/store';
import { notesApi, useUpdateNoteMutation } from '@features/notes';
import { useUser } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';
import { useSnackbar } from 'notistack';

import {
  buildOfficialTimeRepairPatch,
  type FutureDateOfServiceConflict,
  getFutureDateOfServiceConflict,
} from '../utils/futureDateOfService';
import {
  getAuthorMustSubmitMessage,
  getForbiddenMessage,
  getSigningErrorCode,
  getSigningErrorMessage,
  SIGNING_ERROR_SNACKBAR_OPTIONS,
} from '../utils/signingErrors';

/**
 * True when a mutation error is the backend's 409 "note is already co-signed"
 * guard — i.e. this tab's view of the note is stale (e.g. the supervisor
 * co-signed after this tab last loaded it).
 */
const isAlreadyCosignedConflict = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 409;

/** Which finalize action tripped the future-DOS guard, for retry wording. */
export type FutureDosAction = 'sign' | 'complete' | 'resubmit';

export interface FutureDosConflictState extends FutureDateOfServiceConflict {
  action: FutureDosAction;
}

interface UseNoteSigningOptions {
  noteId: string;
  encounterId?: string;
  hasPatient?: boolean;
  hasNoteSigning?: boolean;
  isSigned?: boolean;
  needsCosign?: boolean;
  onNoteFinalized?: () => void | Promise<void>;
  /**
   * The note's actual recording moment (transcription window), used to build
   * the "Update date of service" repair. Without it the dialog can only offer
   * sign-anyway when the encounter clock has been overwritten.
   */
  recordingWindow?: RecordingWindow | null;
}

interface UseNoteSigningReturn {
  /** Whether the signing onboarding dialog is open */
  isOnboardingOpen: boolean;
  /** Set onboarding dialog open state */
  setIsOnboardingOpen: (open: boolean) => void;
  /** Whether a note update is in progress */
  isUpdatingNote: boolean;
  /** Handle Sign Note button click - signs with signature or completes without */
  handleSignNote: () => Promise<void>;
  /** Handle Amend Note from completed mode */
  handleAmendNote: () => Promise<void>;
  /** Handle onboarding completion - signs note after onboarding */
  handleOnboardingComplete: () => Promise<void>;
  /** Handle resubmit for co-sign (from edits_requested → ready) */
  handleResubmit: () => Promise<void>;
  /** GDN-2784: pending future-date-of-service conflict, or null. */
  futureDosConflict: FutureDosConflictState | null;
  /** Repair the official time to the actual recording moment, then retry. */
  confirmFutureDosUpdateDate: () => Promise<void>;
  /** Retry the finalize with the acknowledge flag (keep the future date). */
  confirmFutureDosProceedAnyway: () => Promise<void>;
  /** Close the future-DOS dialog without finalizing. */
  dismissFutureDosConflict: () => void;
  /** Whether the official-time repair is in flight. */
  isRepairingOfficialTime: boolean;
  /**
   * GDN-3911: the backend's verbatim 403 `author_must_submit_first` message,
   * once it has refused a finalize on this note. Null until then. Drives the
   * disabled "Waiting for {author} to submit" affordance — see the docblock
   * on `useCosignContext` for why this has to come from the backend.
   */
  submissionBlockedMessage: string | null;
}

export function useNoteSigning({
  noteId,
  encounterId,
  hasPatient = false,
  hasNoteSigning = false,
  isSigned = false,
  needsCosign = false,
  onNoteFinalized,
  recordingWindow = null,
}: UseNoteSigningOptions): UseNoteSigningReturn {
  const { enqueueSnackbar } = useSnackbar();
  const { note: noteMetrics } = useEventTracking();
  const { userState } = useUser();
  const dispatch = useAppDispatch();

  // Local state for note signing onboarding dialog
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);

  // GDN-2784: pending future-date-of-service guard conflict.
  const [futureDosConflict, setFutureDosConflict] =
    useState<FutureDosConflictState | null>(null);

  // GDN-3911: the backend's "the author must submit this first" refusal,
  // retained per note so the sign affordance can settle into its real
  // disabled state instead of re-offering an action that cannot succeed.
  //
  // The refusal is stored WITH the note it belongs to, and read back only when
  // the ids still match. This hook instance survives navigation between notes,
  // and an effect-based reset runs after the first render — long enough to
  // paint note A's disabled "Waiting for {author}…" over note B, which may be
  // perfectly signable. Deriving it keyed by id means note B never renders the
  // stale affordance at all, because there is no interim state to render.
  const [blockedSubmission, setBlockedSubmission] = useState<{
    noteId: string;
    message: string;
  } | null>(null);
  const submissionBlockedMessage =
    blockedSubmission && blockedSubmission.noteId === noteId ? blockedSubmission.message : null;
  const [getEncounter] = useLazyGetEncounterQuery();
  const [updateEncounter, { isLoading: isRepairingOfficialTime }] =
    useUpdateEncounterMutation();

  /**
   * Handle the sign guard's 409: open the confirm dialog instead of surfacing
   * an error. Must run BEFORE the status-only already-co-signed check.
   * Returns true when the error was the guard's.
   */
  const handleFutureDateOfService = useCallback(
    (error: unknown, action: FutureDosAction): boolean => {
      const conflict = getFutureDateOfServiceConflict(error);
      if (!conflict) return false;
      setFutureDosConflict({ ...conflict, action });
      noteMetrics.futureDosDialogOpened?.(noteId, {
        encounter_id: encounterId,
        days_in_future: conflict.daysInFuture,
        action,
      });
      return true;
    },
    [encounterId, noteId, noteMetrics],
  );

  /**
   * Handle the backend's 409 "already co-signed" guard: this tab was stale.
   * Refetch the note so the UI shows the true signed state (and the sign
   * affordances disappear). Returns true when the error was handled.
   */
  const handleAlreadyCosigned = useCallback(
    (error: unknown): boolean => {
      if (!isAlreadyCosignedConflict(error)) return false;
      enqueueSnackbar(
        'This note has already been co-signed — refreshing to the latest version.',
        { variant: 'info' },
      );
      dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
      return true;
    },
    [dispatch, enqueueSnackbar, noteId],
  );

  /**
   * Surface a finalize/reopen failure, preferring whatever the backend
   * actually said over the generic fallback. Three tiers, in order:
   *
   * 1. GDN-3288 / GDN-3911 enumerated codes (`pdf_render_failed`,
   *    `compliance_unavailable`, `author_must_submit_first`): the message
   *    names the offending section, the recovery step, or the person who
   *    still has to act, so it shows verbatim and holds for 10s.
   * 2. GDN-3911: ANY other 403 carrying a message — the author-only reopen
   *    guard, and whatever future refusals the finalize endpoints add. These
   *    are considered refusals written for the reader; a generic "Failed to
   *    sign note. Please try again." actively misleads, because retrying is
   *    exactly what will not help.
   * 3. Everything else keeps the caller's fallback.
   */
  const showSigningFailure = useCallback(
    (error: unknown, fallbackMessage: string) => {
      // Latch the one refusal that is also a fact about the note, before
      // deciding what to show. Every finalize path funnels through here, so
      // this is the only place that needs to know.
      const mustSubmitFirst = getAuthorMustSubmitMessage(error);
      // Stamped with the note it is about, so navigating away cannot carry it.
      if (mustSubmitFirst) setBlockedSubmission({ noteId, message: mustSubmitFirst });

      const signingErrorMessage = getSigningErrorMessage(error);
      if (signingErrorMessage) {
        enqueueSnackbar(signingErrorMessage, SIGNING_ERROR_SNACKBAR_OPTIONS);
        return;
      }
      // ANY 403 the backend explains — `not_authorized` ("Only the note's
      // author can sign or complete this note"), the author-only reopen guard,
      // and whatever else the finalize endpoints add. Held as long as the coded
      // refusals: it names who may act, which a 3s flash does not convey, and a
      // transient dialog must not be able to swallow it entirely.
      const forbiddenMessage = getForbiddenMessage(error);
      if (forbiddenMessage) {
        enqueueSnackbar(forbiddenMessage, SIGNING_ERROR_SNACKBAR_OPTIONS);
        return;
      }
      enqueueSnackbar(fallbackMessage, { variant: 'error' });
    },
    [enqueueSnackbar, noteId],
  );

  // RTK Query mutation
  const [updateNote, { isLoading: isUpdatingNote }] = useUpdateNoteMutation();

  const notifyNoteFinalized = useCallback(() => {
    if (!onNoteFinalized) return;
    void Promise.resolve(onNoteFinalized()).catch((error: unknown) => {
      console.error('Failed to run note finalization callback:', error);
    });
  }, [onNoteFinalized]);

  /**
   * Perform note signing operation (with electronic signature)
   * Used when the org enables note signing and the note has a patient
   *
   * NOTE: Only updates completion_status. Backend should handle review_status
   * based on completion_status change (e.g., set to 'archived' when completed).
   */
  const performNoteSign = useCallback(async (acknowledgeFutureDos = false) => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
          ...(acknowledgeFutureDos
            ? { acknowledge_future_date_of_service: true }
            : {}),
        },
      }).unwrap();

      notifyNoteFinalized();

      // Show success message
      enqueueSnackbar(
        needsCosign ? 'Note submitted for co-signature' : 'Note signed successfully!',
        { variant: 'success' },
      );

      // Track analytics
      noteMetrics.sign(noteId, {
        has_patient: hasPatient,
        source: 'note-top-bar-edit',
      });
    } catch (error) {
      if (handleFutureDateOfService(error, 'sign')) return;
      if (handleAlreadyCosigned(error)) return;
      console.error('Failed to sign note:', error);
      showSigningFailure(error, 'Failed to sign note. Please try again.');

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        error_code: getSigningErrorCode(error) ?? undefined,
        action_type: 'sign',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, notifyNoteFinalized, enqueueSnackbar, showSigningFailure, noteMetrics, hasPatient, encounterId, needsCosign, handleAlreadyCosigned, handleFutureDateOfService]);

  /**
   * Perform note completion (without electronic signature)
   * Used when the org disables note signing or the note has no patient
   *
   * NOTE: Only updates completion_status. Backend should handle review_status
   * based on completion_status change (e.g., set to 'archived' when completed).
   */
  const performNoteComplete = useCallback(async (acknowledgeFutureDos = false) => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
          ...(acknowledgeFutureDos
            ? { acknowledge_future_date_of_service: true }
            : {}),
        },
      }).unwrap();

      notifyNoteFinalized();

      if (needsCosign) {
        enqueueSnackbar('Note submitted for co-signature', { variant: 'success' });
      }

      // Note completed successfully - no additional tracking needed
      // (completion without signing is a valid outcome)
    } catch (error) {
      if (handleFutureDateOfService(error, 'complete')) return;
      if (handleAlreadyCosigned(error)) return;
      console.error('Failed to complete note:', error);
      showSigningFailure(error, 'Failed to complete note. Please try again.');

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        error_code: getSigningErrorCode(error) ?? undefined,
        action_type: 'complete',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, notifyNoteFinalized, enqueueSnackbar, showSigningFailure, noteMetrics, encounterId, needsCosign, handleAlreadyCosigned, handleFutureDateOfService]);

  /**
   * Handle Sign Note button click
   * - When org note signing is on + patient: signs with electronic signature (shows onboarding if needed)
   * - For other users: completes without signature
   */
  const handleSignNote = useCallback(async () => {
    if (!noteId) return;

    // Track sign/complete click
    noteMetrics.reviewSignClicked?.(noteId, {
      has_patient: hasPatient,
      has_note_signing: hasNoteSigning,
      encounter_id: encounterId,
    });

    if (hasNoteSigning && hasPatient) {
      // Users with signing capability - check if onboarding is needed first
      if (!userState?.onboardingSteps?.note_signing_onboarding_completed) {
        setIsOnboardingOpen(true);
        return; // Show onboarding instead of signing
      }

      // Perform the signing operation
      await performNoteSign();
    } else {
      // Users without signing capability - complete directly
      // Track Complete click
      noteMetrics.completeClicked?.(noteId, {
        has_patient: hasPatient,
        source: 'note-top-bar-edit',
        encounter_id: encounterId,
      });

      await performNoteComplete();
    }
  }, [
    noteId,
    hasPatient,
    hasNoteSigning,
    encounterId,
    noteMetrics,
    userState,
    performNoteSign,
    performNoteComplete,
  ]);

  /**
   * Handle Amend Note from completed mode
   * Reopens a completed note for editing
   */
  const handleAmendNote = useCallback(async () => {
    if (!noteId) return;

    // Track Amend click
    noteMetrics.amendClicked?.(noteId, {
      was_signed: isSigned,
      encounter_id: encounterId,
    });

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'reviewing',
        },
      }).unwrap();

      // Note amended successfully - status changed back to reviewing
    } catch (error) {
      console.error('Failed to amend note:', error);
      // The author-only reopen guard's 403 is handled inside showSigningFailure
      // now (tier 2), along with every other coded or forbidden refusal.
      showSigningFailure(error, 'Failed to reopen note. Please try again.');

      // Track completion failed
      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        error_code: getSigningErrorCode(error) ?? undefined,
        action_type: 'amend',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, showSigningFailure, noteMetrics, isSigned, encounterId]);

  /**
   * Handle onboarding completion
   * Called when user finishes the signing onboarding flow
   * Automatically signs the note after onboarding completes
   */
  const handleOnboardingComplete = useCallback(async () => {
    // Close the onboarding dialog
    setIsOnboardingOpen(false);

    // Perform the signing operation
    await performNoteSign();
  }, [performNoteSign]);

  /**
   * Handle resubmit for co-sign (from edits_requested → completed)
   * Used when author has addressed supervisor feedback and is resubmitting.
   * Backend routes to "ready" internally based on requires_cosigning.
   */
  const performResubmit = useCallback(async (acknowledgeFutureDos = false) => {
    if (!noteId) return;

    try {
      await updateNote({
        noteId,
        updateNoteRequest: {
          completion_status: 'completed',
          ...(acknowledgeFutureDos
            ? { acknowledge_future_date_of_service: true }
            : {}),
        },
      }).unwrap();

      enqueueSnackbar('Note resubmitted for co-signature', { variant: 'success' });
    } catch (error) {
      if (handleFutureDateOfService(error, 'resubmit')) return;
      if (handleAlreadyCosigned(error)) return;
      console.error('Failed to resubmit note:', error);
      showSigningFailure(error, 'Failed to resubmit note. Please try again.');

      noteMetrics.completionFailed?.(noteId, {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        error_code: getSigningErrorCode(error) ?? undefined,
        action_type: 'complete',
        encounter_id: encounterId,
      });
    }
  }, [noteId, updateNote, enqueueSnackbar, showSigningFailure, noteMetrics, encounterId, handleAlreadyCosigned, handleFutureDateOfService]);

  // Zero-arg public wrapper: consumers use this as an event handler, and an
  // event object must never read as the acknowledge flag.
  const handleResubmit = useCallback(async () => performResubmit(false), [performResubmit]);

  /** Retry the action that tripped the guard. */
  const retryConflictAction = useCallback(
    async (action: FutureDosAction, acknowledgeFutureDos: boolean) => {
      if (action === 'sign') await performNoteSign(acknowledgeFutureDos);
      else if (action === 'complete') await performNoteComplete(acknowledgeFutureDos);
      else await performResubmit(acknowledgeFutureDos);
    },
    [performNoteSign, performNoteComplete, performResubmit],
  );

  /**
   * "Update date of service & sign": stamp the actual recording moment as the
   * encounter's official time (in_room), then retry the finalize — the guard
   * passes because the date of service is now today. Refuses (and keeps the
   * dialog open) when no honest repair exists.
   */
  const confirmFutureDosUpdateDate = useCallback(async () => {
    const conflict = futureDosConflict;
    if (!conflict || !encounterId) return;

    noteMetrics.futureDosUpdateDateClicked?.(noteId, {
      encounter_id: encounterId,
      days_in_future: conflict.daysInFuture,
      action: conflict.action,
    });

    try {
      const encounter = await getEncounter({ encounterId }).unwrap();
      const patch = buildOfficialTimeRepairPatch(encounter, recordingWindow);
      if (!patch) {
        enqueueSnackbar(
          'The actual session time could not be determined. Sign anyway, or adjust the session time on the encounter first.',
          { variant: 'warning' },
        );
        return;
      }
      await updateEncounter({ encounterId, encounterUpdate: patch }).unwrap();
    } catch (error) {
      console.error('Failed to update the date of service:', error);
      enqueueSnackbar('Could not update the date of service. Please try again.', {
        variant: 'error',
      });
      return;
    }

    setFutureDosConflict(null);
    await retryConflictAction(conflict.action, false);
  }, [
    futureDosConflict,
    encounterId,
    noteId,
    noteMetrics,
    getEncounter,
    recordingWindow,
    updateEncounter,
    enqueueSnackbar,
    retryConflictAction,
  ]);

  /** "Sign anyway": keep the future date, resend with the acknowledge flag. */
  const confirmFutureDosProceedAnyway = useCallback(async () => {
    const conflict = futureDosConflict;
    if (!conflict) return;

    noteMetrics.futureDosProceedAnywayClicked?.(noteId, {
      encounter_id: encounterId,
      days_in_future: conflict.daysInFuture,
      action: conflict.action,
    });

    setFutureDosConflict(null);
    await retryConflictAction(conflict.action, true);
  }, [futureDosConflict, noteId, encounterId, noteMetrics, retryConflictAction]);

  const dismissFutureDosConflict = useCallback(() => {
    setFutureDosConflict(null);
  }, []);

  return {
    isOnboardingOpen,
    setIsOnboardingOpen,
    isUpdatingNote,
    handleSignNote,
    handleAmendNote,
    handleOnboardingComplete,
    handleResubmit,
    futureDosConflict,
    confirmFutureDosUpdateDate,
    confirmFutureDosProceedAnyway,
    dismissFutureDosConflict,
    isRepairingOfficialTime,
    submissionBlockedMessage,
  };
}
